import React, { useRef, useState } from 'react';
import { format } from 'date-fns';
import { BookOpen, Clock, Calendar, Trophy, Flame, BookMarked, Star, Zap, Share2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { CoverImage } from '@/components/CoverImage';
import { MonthlyReportStats } from '@/lib/monthlyReportUtils';
import html2canvas from 'html2canvas';
import { toast } from '@/hooks/use-toast';

function StarRatingDisplay({ rating }: { rating: number }) {
  return (
    <div className="flex gap-px">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={`w-2.5 h-2.5 ${i <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground/30'}`}
        />
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, label, value }: {
  icon: React.ElementType;
  label: string;
  value: string | number;
}) {
  return (
    <div className="bg-card/80 backdrop-blur-sm rounded-xl p-3 sm:p-4 flex flex-col items-center gap-1 border border-border/20">
      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
      <span className="text-[9px] sm:text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</span>
      <span className="text-lg sm:text-2xl font-bold text-foreground font-heading">{value}</span>
    </div>
  );
}

interface MonthlyReportCardProps {
  stats: MonthlyReportStats;
  selectedDate: Date;
  showShareButton?: boolean;
}

export function MonthlyReportCard({ stats, selectedDate, showShareButton = true }: MonthlyReportCardProps) {
  const reportRef = useRef<HTMLDivElement>(null);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    if (!reportRef.current) return;
    setIsExporting(true);
    try {
      const canvas = await html2canvas(reportRef.current, {
        backgroundColor: null,
        scale: 2,
        useCORS: true,
        allowTaint: true,
        logging: false,
      });
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob(b => resolve(b!), 'image/png', 1.0);
      });

      if (navigator.share && navigator.canShare?.({ files: [new File([blob], 'report.png', { type: 'image/png' })] })) {
        const file = new File([blob], `dino-reads-${format(selectedDate, 'MMMM-yyyy').toLowerCase()}.png`, { type: 'image/png' });
        await navigator.share({
          title: `My ${format(selectedDate, 'MMMM yyyy')} Reading Report`,
          text: `Check out my reading stats from ${format(selectedDate, 'MMMM yyyy')}! 🦕📚`,
          files: [file],
        });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `dino-reads-${format(selectedDate, 'MMMM-yyyy').toLowerCase()}.png`;
        a.click();
        URL.revokeObjectURL(url);
        toast({ title: 'Report downloaded!', description: 'Share it on your favourite platform 🦕' });
      }
    } catch (err) {
      console.error('Export error:', err);
      toast({ title: 'Export failed', description: 'Could not generate the image.', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div>
      <div
        ref={reportRef}
        className="rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary)/0.7) 50%, hsl(142 50% 35%) 100%)',
          padding: '2px',
        }}
      >
        <div className="bg-card rounded-2xl p-4 sm:p-5 space-y-3 sm:space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2 mb-0.5 sm:mb-1">
                <img src="/dino-logo.png" alt="Dino Reads" className="w-6 h-6 sm:w-8 sm:h-8" />
                <span className="font-heading font-bold text-primary text-xs sm:text-sm">Dino Reads</span>
              </div>
              <h1 className="font-heading text-lg sm:text-xl font-bold text-foreground">Monthly Reading Report</h1>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {format(selectedDate, 'MMMM yyyy')}
              </p>
            </div>
            <div className="text-3xl sm:text-4xl">🦕</div>
          </div>

          {/* Top stats grid */}
          <div className="grid grid-cols-2 gap-2">
            <StatCard
              icon={Clock}
              label="Focus Time"
              value={stats.totalFocusMinutes >= 60
                ? `${Math.floor(stats.totalFocusMinutes / 60)}h ${stats.totalFocusMinutes % 60}m`
                : `${stats.totalFocusMinutes}m`}
            />
            <StatCard icon={BookOpen} label="Pages Read" value={stats.totalPages.toLocaleString()} />
            <StatCard icon={Calendar} label="Reading Days" value={`${stats.readingDays} of ${stats.totalDaysInMonth}`} />
            <StatCard icon={BookMarked} label="Books Finished" value={stats.totalBooksFinished} />
          </div>

          {/* Secondary stats */}
          <div className="grid grid-cols-2 gap-2">
            <StatCard icon={Flame} label="Avg Pages / Day" value={stats.avgPagesPerDay} />
            <StatCard icon={Zap} label="Best Day" value={stats.maxPagesInADay > 0 ? `${stats.maxPagesInADay} pg` : '—'} />
          </div>

          {/* Highlight cards */}
          {stats.fastestBook && (
            <div className="bg-primary/5 border border-primary/10 rounded-xl p-3 flex items-center gap-3">
              {stats.fastestBook.coverUrl && (
                <div className="w-12 h-16 flex-shrink-0 rounded-md overflow-hidden">
                  <CoverImage coverUrl={stats.fastestBook.coverUrl || undefined} title={stats.fastestBook.title} size="xs" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-primary font-semibold flex items-center gap-1">
                  <Trophy className="w-3 h-3" /> Fastest Read
                </p>
                <p className="text-sm font-semibold text-foreground truncate">{stats.fastestBook.title}</p>
                <p className="text-xs text-muted-foreground">
                  {stats.fastestBook.daysToRead} day{stats.fastestBook.daysToRead !== 1 ? 's' : ''} · {stats.fastestBook.authors.join(', ')}
                </p>
              </div>
            </div>
          )}

          {stats.longestBook && (
            <div className="bg-primary/5 border border-primary/10 rounded-xl p-3 flex items-center gap-3">
              {stats.longestBook.coverUrl && (
                <div className="w-12 h-16 flex-shrink-0 rounded-md overflow-hidden">
                  <CoverImage coverUrl={stats.longestBook.coverUrl || undefined} title={stats.longestBook.title} size="xs" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-wider text-primary font-semibold flex items-center gap-1">
                  <BookOpen className="w-3 h-3" /> Longest Book
                </p>
                <p className="text-sm font-semibold text-foreground truncate">{stats.longestBook.title}</p>
                <p className="text-xs text-muted-foreground">{stats.longestBook.pageCount.toLocaleString()} pages</p>
              </div>
            </div>
          )}

          {/* Books gallery */}
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium mb-2">Books This Month</p>
            <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${stats.booksRead.length > 8 ? '56px' : '64px'}, 1fr))` }}>
              {stats.booksRead.map(book => (
                <div key={book.id} className="flex flex-col items-center gap-1">
                  <div className="w-full aspect-[2/3] rounded-md overflow-hidden bg-muted/30 shadow-sm">
                    {book.coverUrl ? (
                      <CoverImage coverUrl={book.coverUrl || undefined} title={book.title} size="xs" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                        <BookOpen className="w-5 h-5" />
                      </div>
                    )}
                  </div>
                  <p className="text-[8px] text-muted-foreground text-center leading-tight line-clamp-2 w-full px-0.5">{book.title}</p>
                  {book.rating && <StarRatingDisplay rating={book.rating} />}
                </div>
              ))}
            </div>
          </div>

          {/* Footer watermark */}
          <div className="flex items-center justify-center gap-1.5 pt-2 border-t border-border/20">
            <img src="/dino-logo.png" alt="" className="w-4 h-4 opacity-50" />
            <span className="text-[9px] text-muted-foreground/60 font-medium tracking-wider uppercase">
              dinoreads.com
            </span>
          </div>
        </div>
      </div>

      {showShareButton && (
        <div className="flex justify-center gap-3 mt-4">
          <Button
            onClick={handleExport}
            disabled={isExporting}
            className="flex-1 gap-2"
            size="lg"
          >
            {isExporting ? (
              <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
            ) : navigator.share ? (
              <Share2 className="w-4 h-4" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {navigator.share ? 'Share Report' : 'Download Report'}
          </Button>
        </div>
      )}
    </div>
  );
}
